/**
 * Professional template - polished cover letter format for corporate applications
 * @param {Object} coverLetterData - Cover letter data object
 * @returns {string} HTML formatted cover letter
 */
export const professionalTemplateHtml = (coverLetterData) => {
  if (!coverLetterData) return "";

  const {
    personalInformation,
    recipientName,
    recipientTitle,
    companyName,
    companyAddress,
    greeting,
    openingParagraph,
    bodyParagraphs = [],
    closingParagraph,
    closingPhrase = "Sincerely,",
    signatureName,
    date,
  } = coverLetterData;

  // Format the current date if not provided
  const formattedDate = date || formatDate(new Date());

  // Start with a container that identifies the template
  let html = `<div data-template="professional" class="cover-letter-template">`;

  // Header with name prominently displayed
  if (personalInformation) {
    const { firstName, lastName, address, phone, email, linkedinUrl } =
      personalInformation;
    const fullName = `${firstName || ""} ${lastName || ""}`.trim();

    html += `<div data-section="header" style="border-bottom: 2px solid #2c3e50; padding-bottom: 15px; margin-bottom: 25px;">
      <h1 data-element="sender-name" style="color: #2c3e50; font-size: 24px; margin-bottom: 10px; text-transform: uppercase;">${fullName}</h1>
      <div data-element="contact-info" style="display: flex; flex-wrap: wrap; gap: 15px; font-size: 14px; color: #555;">`;

    if (email) {
      html += `<span style="display: flex; align-items: center;">
        <img src="https://firebasestorage.googleapis.com/v0/b/resumerevival-bb193.appspot.com/o/email.png?alt=media&token=de360c09-caa6-4989-b8c2-e08591b8b808" alt="Email Icon" style="width:16px; margin-right:6px;" /> ${email}
      </span>`;
    }

    if (phone) {
      html += `<span style="display: flex; align-items: center;">
        <img src="https://firebasestorage.googleapis.com/v0/b/resumerevival-bb193.appspot.com/o/phone.png?alt=media&token=350965e3-0c60-4ac8-b1e9-d926dab6563a" alt="Phone Icon" style="width:16px; margin-right:6px;" /> ${phone}
      </span>`;
    }

    if (address) {
      html += `<span style="display: flex; align-items: center;">
        <img src="https://firebasestorage.googleapis.com/v0/b/resumerevival-bb193.appspot.com/o/location_on_24dp_000000_FILL0_wght0_GRAD0_opsz24.png?alt=media&token=6c07d344-d056-46e9-83dc-4e05dd5e566a" alt="Address Icon" style="width:16px; margin-right:6px;" /> ${address}
      </span>`;
    }

    if (linkedinUrl) {
      const cleanUrl = linkedinUrl
        .replace(/https?:\/\/(www\.)?/, "")
        .replace(/\/$/, "");
      html += `<span style="display: flex; align-items: center;">
        <img src="https://firebasestorage.googleapis.com/v0/b/resumerevival-bb193.appspot.com/o/f92d3760fc8301b2129d40b47c6160d5.png?alt=media&token=5bd94ca8-155e-4120-8f0d-4522fe60dbbe" alt="LinkedIn Icon" style="width:16px; margin-right:6px;" /> ${cleanUrl}
      </span>`;
    }

    if (personalInformation.githubUrl) {
      const cleanUrl = personalInformation.githubUrl
        .replace(/https?:\/\/(www\.)?/, "")
        .replace(/\/$/, "");
      html += `<span style="display: flex; align-items: center;">
        <img src="https://firebasestorage.googleapis.com/v0/b/resumerevival-bb193.appspot.com/o/github.svg?alt=media&token=26e77e68-a792-4617-9581-025159bf9e1b" alt="GitHub Icon" style="width:16px; margin-right:6px;" /> ${cleanUrl}
      </span>`;
    }

    if (personalInformation.portfolioUrl) {
      const cleanUrl = personalInformation.portfolioUrl
        .replace(/https?:\/\/(www\.)?/, "")
        .replace(/\/$/, "");
      html += `<span style="display: flex; align-items: center;">
        <img src="https://firebasestorage.googleapis.com/v0/b/resumerevival-bb193.appspot.com/o/link.png?alt=media&token=5d9f6b9e-5b3a-4b99-b1f6-2ee99c4bee75" alt="Portfolio Icon" style="width:16px; margin-right:6px;" /> ${cleanUrl}
      </span>`;
    }

    html += `</div>
    </div>`;
  }

  // Date
  html += `<p data-element="date" style="text-align: right; margin-bottom: 20px; color: #555;">${formattedDate}</p>`;

  // Recipient Information
  html += `<div data-section="recipient-info" style="margin-bottom: 25px;">`;

  if (recipientName) {
    html += `<p data-element="recipient-name" style="margin-bottom: 5px; font-weight: bold;">${recipientName}</p>`;
  }

  if (recipientTitle) {
    html += `<p data-element="recipient-title" style="margin-bottom: 5px;">${recipientTitle}</p>`;
  }

  if (companyName) {
    html += `<p data-element="company-name" style="margin-bottom: 5px; font-weight: bold;">${companyName}</p>`;
  }

  if (companyAddress) {
    html += `<p data-element="company-address" style="margin-bottom: 5px;">${companyAddress}</p>`;
  }

  html += `</div>`;

  // Greeting
  if (greeting) {
    html += `<p data-element="greeting" style="margin-bottom: 20px; font-weight: bold;">${greeting}</p>`;
  } else {
    html += `<p data-element="greeting" style="margin-bottom: 20px; font-weight: bold;">Dear Hiring Manager,</p>`;
  }

  // Opening Paragraph
  if (openingParagraph) {
    html += `<p data-element="opening-paragraph" style="margin-bottom: 15px; text-align: justify; line-height: 1.5;">${openingParagraph}</p>`;
  }

  // Body Paragraphs
  if (bodyParagraphs && bodyParagraphs.length > 0) {
    bodyParagraphs.forEach((paragraph) => {
      if (paragraph && paragraph.trim()) {
        html += `<p data-element="body-paragraph" style="margin-bottom: 15px; text-align: justify; line-height: 1.5;">${paragraph}</p>`;
      }
    });
  }

  // Closing Paragraph
  if (closingParagraph) {
    html += `<p data-element="closing-paragraph" style="margin-bottom: 25px; text-align: justify; line-height: 1.5;">${closingParagraph}</p>`;
  }

  // Closing
  html += `<p data-element="closing-phrase" style="margin-bottom: 50px;">${closingPhrase}</p>`;

  // Signature
  const signature =
    signatureName ||
    (personalInformation
      ? `${personalInformation.firstName || ""} ${
          personalInformation.lastName || ""
        }`.trim()
      : "");

  if (signature) {
    html += `<p data-element="signature" style="font-weight: bold; color: #2c3e50;">${signature}</p>`;
  }

  html += `</div>`; // Close cover-letter-template container

  return html;
};

/**
 * Helper function to format dates consistently
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
const formatDate = (date) => {
  if (!date) return "";

  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;

    // Check if date is valid
    if (isNaN(dateObj.getTime())) {
      return typeof date === "string" ? date : "";
    }

    // Format as Month Day, Year (e.g., "March 15, 2025")
    const options = { month: "long", day: "numeric", year: "numeric" };
    return dateObj.toLocaleDateString(undefined, options);
  } catch (error) {
    // If any error occurs during parsing, return the original string or empty string
    return typeof date === "string" ? date : "";
  }
};

export default professionalTemplateHtml;
